/**
 * ReminderClock — F 490's connector between a date surface and the
 * reminder feature.
 *
 * WHY A CONNECTOR AND NOT `SetReminder` DIRECTLY
 * ----------------------------------------------
 * `SetReminder` needs a store. The store lives in the provider. So a
 * surface that wants a clock would have to reach into the context, handle
 * the "no provider" case, look up the existing reminder for its date, and
 * pass five props. That is a lot of ceremony to repeat at every date
 * surface, and it would be repeated WRONG at some of them.
 *
 * So this component does the wiring ONCE, and a surface says:
 *
 *   <ReminderClock parentNodeId={task.id} dateValue={task.due} />
 *
 * It looks up the existing reminder for that date, so the clock renders as
 * "already has a reminder" when it does.
 *
 * IT RENDERS NOTHING WITHOUT A PROVIDER
 * -------------------------------------
 * A date surface that renders a clock in an app with no reminder provider
 * would show a button that does nothing. So without a provider this
 * component renders `null` — the date surface is unchanged, which is the
 * right behaviour for a feature that is not mounted.
 */
import React, { useEffect, useState } from 'react';
import type { Reminder } from '../../../types/models/Reminder';
import { useOptionalReminderContext } from './ReminderProvider';
import { SetReminder } from './SetReminder';

export interface ReminderClockProps {
  /** The node that owns the date. */
  parentNodeId: string;
  /** WHICH date on that node. Optional. */
  datePropertyId?: string | null;
  /** The date's value. */
  dateValue: string | null | undefined;
  /** The text the notification will carry. */
  text?: string | null;
  /** Render inline (in a row) rather than as its own block. */
  inline?: boolean;
}

export const ReminderClock: React.FC<ReminderClockProps> = ({
  parentNodeId,
  datePropertyId = null,
  dateValue,
  text = null,
  inline = false,
}) => {
  const ctx = useOptionalReminderContext();
  const [existing, setExisting] = useState<Reminder | null>(null);

  // Look the reminder up whenever the date, the node, or the feed changes.
  // The feed is in the dependency list because a reminder that is created
  // in another surface (or by the scheduler) has to show up here.
  useEffect(() => {
    if (!ctx) return;
    let cancelled = false;
    void (async () => {
      try {
        const found = await ctx.store.findFor(parentNodeId, datePropertyId);
        if (!cancelled) setExisting(found);
      } catch {
        if (!cancelled) setExisting(null);
      }
    })();
    return () => { cancelled = true; };
  }, [ctx, ctx?.notifications, parentNodeId, datePropertyId]);

  if (!ctx) return null;
  if (!dateValue) return null;

  return (
    <SetReminder
      parentNodeId={parentNodeId}
      datePropertyId={datePropertyId}
      dateValue={dateValue}
      text={text}
      store={ctx.store}
      existing={existing}
      className={inline ? 'fc-set-reminder--inline' : ''}
    />
  );
};
